import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";
import { Loader2, type LucideIcon } from "lucide-react";
import type { IconType } from "react-icons";
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip";
import type { TooltipContentProps } from "@radix-ui/react-tooltip";

const buttonVariants = cva(
  "qodo qodo-button inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none cursor-pointer border border-transparent",
  {
    variants: {
      variant: {
        primary:
          "bg-fill-action-emphasis-idle text-text-action-on-emphasis-idle hover:bg-fill-action-emphasis-hover focus:bg-fill-action-emphasis-hover active:bg-fill-action-emphasis-hover disabled:bg-fill-action-emphasis-disabled disabled:text-text-action-on-emphasis-disabled",
        secondary:
          "bg-fill-action-neutral-idle text-text-action-neutral-idle hover:bg-fill-action-neutral-hover active:bg-fill-action-neutral-active disabled:bg-fill-action-neutral-disabled disabled:text-text-action-neutral-disabled",
        outline:
          "border border-border-action-neutral-idle text-text-action-neutral-idle! hover:bg-fill-action-neutral-hover active:bg-fill-action-neutral-active disabled:bg-none disabled:text-text-action-neutral-disabled!",
        ghost:
          "text-text-action-neutral-idle hover:bg-fill-action-neutral-hover hover:text-text-action-neutral-hover active:bg-fill-action-neutral-active disabled:bg-none disabled:text-text-action-neutral-disabled",
        link: "text-text-action-emphasis-idle hover:text-text-action-emphasis-hover bg-none disabled:text-text-action-emphasis-disabled",
        danger:
          "border border-border-action-negative-idle text-icon-action-negative-idle! hover:bg-fill-action-negative-hover active:bg-fill-action-negative-active disabled:border-border-action-negative-disabled disabled:text-icon-action-negative-disabled!",
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        xxs: "h-6 rounded-sm px-2 py-0 text-2xs font-normal",
        xs: "h-fit rounded-sm px-2 py-1.5 text-xs font-normal",
        sm: "h-8 rounded-sm px-3 text-sm",
        lg: "h-11 rounded-sm px-8 text-sm",
        "icon-sm": "size-7 p-1.5 rounded-sm",
        "icon-md": "size-8 p-1.5 rounded-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: LucideIcon | IconType;
  iconNode?: React.ReactNode;
  iconPosition?: "left" | "right";
  tooltip?: React.ReactNode;
  tooltipProps?: TooltipContentProps & { disableArrow?: boolean };
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading,
      children,
      disabled,
      icon: Icon,
      iconNode,
      iconPosition = "left",
      tooltip,
      tooltipProps,
      ...props
    },
    ref,
  ) => {
    const iconSizeClass = {
      lg: "size-4.5",
      default: "size-4.5",
      sm: "size-4.5",
      xxs: "size-3",
      xs: "size-3.5",
      "icon-sm": "size-3.5",
      "icon-md": "size-4.5",
    }[size ?? "default"];

    const iconElement = Icon && (
      <Icon
        className={cn(iconSizeClass, "transition-opacity", {
          "opacity-50": disabled || loading,
        })}
      />
    );

    const loaderElement = (
      <Loader2
        className={cn("animate-spin", iconSizeClass, {
          "opacity-50": disabled || loading,
        })}
      />
    );

    const hasIcon = Icon || iconNode != null;
    const resolvedIconSlot =
      loading && hasIcon ? loaderElement : iconElement || iconNode;

    const buttonElement = (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {iconPosition === "left" && resolvedIconSlot}
        {children}
        {iconPosition === "right" && resolvedIconSlot}
        {loading && !hasIcon && loaderElement}
      </button>
    );

    if (tooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* span wrapper ensures tooltip works on disabled buttons */}
            <span className="inline-flex">{buttonElement}</span>
          </TooltipTrigger>
          <TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return buttonElement;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
